import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Inbox, Sparkles, Truck, Send, Trash2, RefreshCw, Gift, ExternalLink, Wand2, Clock } from "lucide-react";

const SourceBadge = ({ source }) => {
  const map = {
    auto_sweep: { label: "Inspired", cls: "bg-yellow-500/10 gold-text border-yellow-500/20" },
    autonomous: { label: "Curated by AI", cls: "bg-primary/15 text-primary border-primary/30" },
    manual: { label: "Hand-picked", cls: "bg-muted/50 text-muted-foreground border-border" },
  };
  const s = map[source] || map.manual;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
};

const Drafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/drafts");
    setDrafts(res.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const res = await api.post("/drafts/sweep");
      await load();
      alert(`Inspiration found — ${res.data.created.length} new curation(s) created, ${res.data.skipped} already existed.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't find inspiration right now.");
    } finally {
      setSweeping(false);
    }
  };

  const approve = async (card, method) => {
    try {
      await api.post(`/cards/${card.id}/approve`, { delivery_method: method });
      await load();
      alert(method === "digital" ? "Delivered digitally!" : "Sent to print & mail. Track under Sent with Love.");
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not send card.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Discard this draft?")) return;
    await api.delete(`/cards/${id}`);
    await load();
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">
            Curated for You <Inbox className="w-7 h-7 text-primary" />
          </h1>
          <p className="text-muted-foreground">
            Thoughtfully curated designs, awaiting your personal touch.
          </p>
        </div>
        <button data-testid="run-sweep-btn" onClick={runSweep} disabled={sweeping}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-60">
          {sweeping ? <><RefreshCw className="w-4 h-4 animate-spin" /> Finding…</> : <><Clock className="w-4 h-4" /> Find Inspiration</>}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading your curations…</div>
      ) : drafts.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Wand2 className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">Nothing curated yet</h3>
          <p className="text-muted-foreground mb-6">
            Find inspiration to curate designs for milestones in the next 14 days,
            or create one in the Design Studio.
          </p>
          <button onClick={runSweep} disabled={sweeping}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-5 py-3 rounded-lg hover:opacity-90 disabled:opacity-60">
            <Clock className="w-4 h-4" /> Find Inspiration
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="drafts-grid">
          {drafts.map((c) => (
            <div key={c.id} className="glass rounded-2xl overflow-hidden card-hover">
              <img src={c.image_thumb_base64 ? `data:image/jpeg;base64,${c.image_thumb_base64}` : `data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-48 object-cover" />
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.occasion}</span>
                    <SourceBadge source={c.source} />
                  </div>
                  {c.milestone_date && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(c.milestone_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
                <div className="font-serif text-lg mb-1">{c.loved_one_name}</div>
                <p className="text-sm text-muted-foreground font-serif italic line-clamp-3 mb-3">{c.message}</p>

                {c.gift_suggestions?.length > 0 && (
                  <div className="mb-3">
                    <button onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className="flex items-center gap-1.5 text-xs text-secondary hover:underline" data-testid="toggle-gifts">
                      <Gift className="w-3.5 h-3.5" /> {c.gift_suggestions.length} matching gift ideas
                    </button>
                    {expanded === c.id && (
                      <div className="mt-2 space-y-2" data-testid="gift-suggestions">
                        {c.gift_suggestions.map((g, i) => (
                          <div key={i} className="bg-muted/30 rounded-lg p-2.5 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{g.name}</span>
                              <span className="text-xs gold-text whitespace-nowrap">{g.price_range}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{g.why}</p>
                            <div className="flex gap-2">
                              <a href={g.etsy_url} target="_blank" rel="noreferrer" className="text-[11px] flex items-center gap-1 text-secondary hover:underline">Etsy <ExternalLink className="w-3 h-3" /></a>
                              <a href={g.shopify_url} target="_blank" rel="noreferrer" className="text-[11px] flex items-center gap-1 text-primary hover:underline">Shop <ExternalLink className="w-3 h-3" /></a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-3 border-t border-border/50">
                  <button onClick={() => approve(c, "digital")} data-testid="draft-digital-btn"
                    className="flex-1 text-xs flex items-center justify-center gap-1 bg-muted/50 border border-secondary/40 text-secondary rounded-lg py-2 hover:bg-secondary/10"><Send className="w-3.5 h-3.5" /> Deliver Digital</button>
                  <button onClick={() => approve(c, "physical")} data-testid="draft-mail-btn"
                    className="flex-1 text-xs flex items-center justify-center gap-1 bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90"><Truck className="w-3.5 h-3.5" /> Approve &amp; Mail</button>
                  <button onClick={() => remove(c.id)} className="text-xs text-muted-foreground hover:text-red-400 px-2"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
};

export default Drafts;
